import type { ChangeEvent } from "react"

export const TIMEZONE_LOCAL = "local"

export const COMMON_TIMEZONES: { value: string; label: string }[] = [
  { value: TIMEZONE_LOCAL, label: "Local" },
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "ET — New York" },
  { value: "America/Chicago", label: "CT — Chicago" },
  { value: "America/Denver", label: "MT — Denver" },
  { value: "America/Los_Angeles", label: "PT — Los Angeles" },
  { value: "America/Anchorage", label: "AKT — Anchorage" },
  { value: "Pacific/Honolulu", label: "HT — Honolulu" },
  { value: "America/Toronto", label: "ET — Toronto" },
  { value: "America/Vancouver", label: "PT — Vancouver" },
  { value: "America/Halifax", label: "AT — Halifax" },
  { value: "America/Sao_Paulo", label: "BRT — São Paulo" },
  { value: "America/Argentina/Buenos_Aires", label: "ART — Buenos Aires" },
  { value: "America/Bogota", label: "COT — Bogotá" },
  { value: "Europe/London", label: "GMT/BST — London" },
  { value: "Europe/Paris", label: "CET — Paris" },
  { value: "Europe/Berlin", label: "CET — Berlin" },
  { value: "Europe/Amsterdam", label: "CET — Amsterdam" },
  { value: "Europe/Helsinki", label: "EET — Helsinki" },
  { value: "Europe/Moscow", label: "MSK — Moscow" },
  { value: "Europe/Istanbul", label: "TRT — Istanbul" },
  { value: "Africa/Cairo", label: "EET — Cairo" },
  { value: "Africa/Johannesburg", label: "SAST — Johannesburg" },
  { value: "Africa/Lagos", label: "WAT — Lagos" },
  { value: "Asia/Dubai", label: "GST — Dubai" },
  { value: "Asia/Kolkata", label: "IST — Kolkata" },
  { value: "Asia/Dhaka", label: "BST — Dhaka" },
  { value: "Asia/Bangkok", label: "ICT — Bangkok" },
  { value: "Asia/Singapore", label: "SGT — Singapore" },
  { value: "Asia/Shanghai", label: "CST — Shanghai" },
  { value: "Asia/Tokyo", label: "JST — Tokyo" },
  { value: "Asia/Seoul", label: "KST — Seoul" },
  { value: "Australia/Sydney", label: "AEDT — Sydney" },
  { value: "Pacific/Auckland", label: "NZDT — Auckland" },
]

interface TimezoneSelectorProps {
  value: string
  onChange: (tz: string) => void
}

export function TimezoneSelector({ value, onChange }: TimezoneSelectorProps) {
  return (
    <select
      value={value}
      onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
      style={{
        fontSize: "0.75rem",
        padding: "4px 6px",
        borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.12)",
        background: "rgba(255,255,255,0.06)",
        color: "inherit",
        cursor: "pointer",
        outline: "none",
        maxWidth: 170,
      }}
    >
      {COMMON_TIMEZONES.map((tz) => (
        <option
          key={tz.value}
          value={tz.value}
          style={{ background: "#1a202c" }}
        >
          {tz.label}
        </option>
      ))}
    </select>
  )
}

/**
 * Pass the result of `localStorage.getItem(...)` (or `null`) and a fallback
 * IANA tz; returns the value to seed `useState` with.
 */
export function resolveStoredTimezone(stored: string | null): string {
  return stored && stored.length > 0 ? stored : TIMEZONE_LOCAL
}

/**
 * Format an ISO timestamp string in the chosen display timezone.
 * `displayTimezone === TIMEZONE_LOCAL` falls back to the browser's local zone.
 */
export function formatInTimezone(
  iso: string | null | undefined,
  displayTimezone: string,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  },
): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleString(undefined, {
    ...options,
    timeZone: displayTimezone === TIMEZONE_LOCAL ? undefined : displayTimezone,
  })
}
